import type { RefObject } from 'react'

import { useEffect, useState } from 'react'

interface InViewOptions {
    once?: boolean
    threshold?: number
    rootMargin?: string
    root?: RefObject<Element | null>
}

/**
 * Tracks whether an element is inside the viewport (or a scrolling root).
 * Always false on the server and the first client render.
 */
export const useInView = (
    ref: RefObject<Element | null>,
    { once = false, threshold = 0.2, rootMargin = '0px', root }: InViewOptions = {}
): boolean => {
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true)

                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, {
            threshold,
            rootMargin,
            root: root?.current ?? null
        })

        observer.observe(element)

        return () => observer.disconnect()
    }, [ref, once, threshold, rootMargin, root])

    return inView
}
